import { useEffect } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface ModalStep {
  number: string
  title: string
  description: string
}

interface StepsModalProps {
  isOpen: boolean
  onClose: () => void
  label: string
  title: string
  steps: ModalStep[]
  footerText: string
  footerCta: string
}

export default function StepsModal({
  isOpen,
  onClose,
  label,
  title,
  steps,
  footerText,
  footerCta,
}: StepsModalProps) {
  useEffect(() => {
    if (!isOpen) { return }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { onClose() }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [isOpen])

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center p-4',
        'bg-foreground/40 transition-opacity duration-200 motion-reduce:transition-none',
        isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
      )}
      role="dialog"
      aria-modal="true"
      aria-labelledby="steps-modal-title"
      onClick={onClose}
    >
      <div
        className={cn(
          'relative w-full max-w-[620px] rounded-2xl bg-background shadow-2xl',
          'transition-transform duration-200 motion-reduce:transition-none',
          isOpen ? 'scale-100' : 'scale-95',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-5 top-5 flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          aria-label="Close"
        >
          <X className="size-4" />
        </button>

        <div className="px-10 py-10">
          <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.2em] text-primary">
            {label}
          </p>
          <h2
            id="steps-modal-title"
            className="mb-10 font-heading text-[32px] font-bold leading-[1.1] tracking-[-0.025em] text-foreground"
          >
            {title}
          </h2>

          <ol className="flex flex-col gap-8">
            {steps.map((step) => (
              <li key={step.number} className="flex gap-5">
                <span className="shrink-0 pt-0.5 font-mono text-[13px] font-semibold text-primary">
                  {step.number}
                </span>
                <div className="border-l-2 border-border pl-5">
                  <h3 className="mb-1.5 font-heading text-[17px] font-bold leading-snug text-foreground">
                    {step.title}
                  </h3>
                  <p className="font-sans text-[14px] leading-[1.65] text-muted-foreground">
                    {step.description}
                  </p>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-10 flex items-center justify-between border-t border-border pt-7">
            <p className="font-sans text-[13px] text-muted-foreground">{footerText}</p>
            <button
              onClick={onClose}
              className="font-sans text-[13px] font-semibold text-primary transition-opacity hover:opacity-70"
            >
              {footerCta}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
